// src/components/board/ExportPostStats.tsx
import React, { useState } from "react";
import {
    Avatar,
    Badge,
    Button,
    Empty,
    Input,
    List,
    Typography,
    Popover,
    Result,
    Space,
    Spin,
    Tooltip,
    notification,
    MenuProps,
    Dropdown,
    Progress
} from "antd";
import {
    QuestionCircleOutlined,
    UpOutlined,
    DownOutlined,
    EditOutlined,
    CopyOutlined,
    CheckOutlined,
    CloseOutlined
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";

// @ts-ignore
import UsersManualExport from '@documents/UsersManual_ExportPostStats.pdf';
import { PostDao } from "@repo/dao/PostDao";
import { IPost } from "@repo/types/IPost";
import uniqid from 'uniqid';
import moment from "moment";
import BaseGrid from "@components/reactgrid/BaseGrid";
import { detectMessageLink } from "@/services/platform-detector.service";

const { Paragraph, Text } = Typography;

enum Status {
    isBasic,
    isEmpty,
    isLoading,
    isContent,
    isError
}

const UsersManualExportButton: React.FC<any> = () => {
    const { t } = useTranslation()
    return (
        <div style={{ textAlign: "left", width: '100%' }}>
            <Text color="#d9d9d9">{t(`ExportPostStats.search.pleaseRead`)}
                <a href={UsersManualExport} rel="noopener noreferrer" target="_blank">
                    <Button
                        type="link"
                        style={{ paddingLeft: 4, paddingRight: 0, color: "#9261f1" }}>
                        {t(`ExportPostStats.search.manual`)}
                    </Button>
                </a>
            </Text>
        </div>
    )
}

const SpinLoading: React.FC<{ progress: number }> = ({ progress }) => (
    <Spin
        tip="Loading"
        size="large"
        style={{ marginTop: -40, marginBottom: 0 }}
    >
        <div style={{
            padding: 50,
            marginTop: 80,
            marginBottom: 80,
            background: 'rgba(0, 0, 0, 0)',
            borderRadius: 4,
        }} />
        <Progress percent={progress} />
    </Spin>
)

const ExportPostStats: React.FC<any> = () => {
    const [progress, setProgress] = useState<number>(0);
    const columns: Array<any> = [
        { title: 'Статус', dataIndex: 'status', key: 'status', width: 80 },
        { title: 'Дата публикации', dataIndex: 'publicationDate', key: 'publicationDate', width: 80 },
        { title: 'Время публикации', dataIndex: 'publicationTime', key: 'publicationTime', width: 80 },
        { title: 'Дата удаления', dataIndex: 'deletionDate', key: 'deletionDate', width: 80 },
        { title: 'Время удаления', dataIndex: 'deletionTime', key: 'deletionTime', width: 80 },
        { title: 'Показы 12', dataIndex: 'views12', key: 'views12', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Показы 24', dataIndex: 'views24', key: 'views24', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Показы 48', dataIndex: 'views48', key: 'views48', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Показы', dataIndex: 'viewsCount', key: 'viewsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Платформа', dataIndex: 'platform', key: 'platform', width: 80 },
        { title: 'URL поста', dataIndex: 'link', key: 'link', width: 80, style: { justifyContent: 'flex-start' }, },
        { title: 'Likes', dataIndex: 'reactionsCount', key: 'reactionsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Shares', dataIndex: 'sharesCount', key: 'sharesCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Пересылки', dataIndex: 'forwardsCount', key: 'forwardsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Комментарии', dataIndex: 'commentsCount', key: 'commentsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'ERID', dataIndex: 'messageErid', key: 'messageErid', width: 80 },
        { title: 'Длина', dataIndex: 'messageLength', key: 'messageLength', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Ссылки', dataIndex: 'messageLinks', key: 'messageLinks', width: 80, style: { justifyContent: 'flex-start' } },
        { title: 'Ссылки TG', dataIndex: 'messageLinksTg', key: 'messageLinksTg', width: 80, style: { justifyContent: 'flex-start' } },
    ];

    const ColumnsForBufferAll: Array<any> = [
        { title: 'Статус', key: 'status', format: (e: any) => e },
        { title: 'Дата публикации', key: 'publicationDate', format: (e: any) => e },
        { title: 'Время публикации', key: 'publicationTime', format: (e: any) => e },
        { title: 'Дата удаления', key: 'deletionDate', format: (e: any) => e },
        { title: 'Время удаления', key: 'deletionTime', format: (e: any) => e },
        { title: 'Показы 12', key: 'views12', format: (e: any) => e },
        { title: 'Показы 24', key: 'views24', format: (e: any) => e },
        { title: 'Показы 48', key: 'views48', format: (e: any) => e },
        { title: 'Показы', key: 'viewsCount', format: (e: any) => e },
        { title: 'Платформа', key: 'platform', format: (e: any) => e },
        { title: 'URL поста', key: 'link', format: (e: any) => e },
        { title: 'Likes', key: 'reactionsCount', format: (e: any) => e },
        { title: 'Shares', key: 'sharesCount', format: (e: any) => e },
        { title: 'Пересылки', key: 'forwardsCount', format: (e: any) => e },
        { title: 'Комментарии', key: 'commentsCount', format: (e: any) => e },
        { title: 'ERID', key: 'messageErid', format: (e: any) => e },
        { title: 'Длина', key: 'messageLength', format: (e: any) => e },
        { title: 'Ссылки', key: 'messageLinks', format: (e: any) => e },
        { title: 'Ссылки TG', key: 'messageLinksTg', format: (e: any) => e },
    ];

    const ColumnsForBuffer: Array<any> = [
        { title: 'Статус', key: 'status', format: (e: any) => e },
        { title: 'Дата публикации', key: 'publicationDate', format: (e: any) => e },
        { title: 'Время публикации', key: 'publicationTime', format: (e: any) => e },
        { title: 'Дата удаления', key: 'deletionDate', format: (e: any) => e },
        { title: 'Время удаления', key: 'deletionTime', format: (e: any) => e },
        { title: 'Показы', key: 'viewsCount', format: (e: any) => e },
        { title: 'URL поста', key: 'link', format: (e: any) => e },
        { title: 'Likes', key: 'reactionsCount', format: (e: any) => e },
        { title: 'Shares', key: 'sharesCount', format: (e: any) => e },
        { title: 'Комментарии', key: 'commentsCount', format: (e: any) => e },
    ];

    const ColumnsForBufferAnalytics: Array<any> = [
        ...ColumnsForBuffer,
        { title: 'ERID', key: 'messageErid', format: (e: any) => e },
        { title: 'Длина', key: 'messageLength', format: (e: any) => e },
        { title: 'Ссылки', key: 'messageLinks', format: (e: any) => e },
        { title: 'Ссылки TG', key: 'messageLinksTg', format: (e: any) => e },
    ]

    const ColumnsForBufferAnalytics2: Array<any> = [
        { title: 'Статус', key: 'status', format: (e: any) => e },
        { title: 'Дата публикации', key: 'publicationDate', format: (e: any) => e },
        { title: 'День недели', key: 'publicationDoW', format: (e: any) => e },
        { title: 'Время публикации', key: 'publicationTime', format: (e: any) => e },
        { title: 'Дата удаления', key: 'deletionDate', format: (e: any) => e },
        { title: 'Время удаления', key: 'deletionTime', format: (e: any) => e },
        { title: 'Показы', key: 'viewsCount', format: (e: any) => e },
        { title: 'Показы 24', key: 'views24', format: (e: any) => e },
        { title: 'Показы 48', key: 'views48', format: (e: any) => e },
        { title: 'URL поста', key: 'link', format: (e: any) => e },
        { title: 'Likes', key: 'reactionsCount', format: (e: any) => e },
        { title: 'Shares', key: 'sharesCount', format: (e: any) => e },
        { title: 'Комментарии', key: 'commentsCount', format: (e: any) => e },
    ]


    const [status, setStatus] = useState<Status>(Status.isEmpty)
    const [search, setSearch] = useState<Array<string>>(JSON.parse(localStorage.getItem('ExportPostStats.search') || '[]'))
    const [content, setContent] = useState<Array<any>>([])
    const [searchArrayOpened, setSearchArrayOpened] = useState<Number>(0)

    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();

    const handleSearchArrayOpenedChange = (value: any) => {
        localStorage.setItem('ExportPostStats.searchArrayOpened', String(value))
        setSearchArrayOpened(Number(value));
    };
    const handleSearchClear = () => {
        localStorage.removeItem('ExportPostStats.search')
        setSearch([])
    }
    const handleSearchChange = (val: string) => {
        const value = val.replaceAll(/\?single$/gi, '')
        // Убираем жёсткую фильтрацию только для t.me, принимаем любые ссылки, которые поддерживаются платформами
        const links = value
            .split(/[\t\n\s]+/)
            .filter(Boolean)
            .map(w => w.trim())
            .filter(link => {
                const platform = detectMessageLink(link);
                return platform === 'telegram' || platform === 'max';
            });
        setSearch(links);
        localStorage.setItem('ExportPostStats.search', JSON.stringify(links))
    };
    const handleSearch = async () => {
        setProgress(0)
        setStatus(Status.isLoading)
        setContent([])
        const result = []
        const total = search.length

        for (const url of search) {
            const platform = detectMessageLink(url);
            if (!platform || (platform !== 'telegram' && platform !== 'max')) {
                result.push({
                    error: true,
                    status: t('ExportPostStats.post.unsupportedPlatform'),
                    title: url,
                });
                setProgress((prev) => Math.round(prev + 100 / total));
                continue;
            }

            try {
                const post = await PostDao.getOne(url, platform);
                result.push(post);
            } catch (error: any) {
                result.push({
                    error: true,
                    status: t('ExportPostStats.post.requestError'),
                    title: url,
                    message: error.message,
                });
            }

            setProgress((prev) => Math.round(prev + 100 / total));
        }

        setContent(result)
        setStatus(Status.isContent)
    }

    function removeHTMLTags(htmlString: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const textContent = doc.body.textContent || "";
        return textContent.trim();
    }

    const getObjectForUser = (post: IPost): object => {
        if (!post.platform_id) return {
            status: t('ExportPostStats.publication.isNotFound'),
        }
        const links = [
            ...(
                new Set(
                    String(post.text)
                        ?.match(/href="?'?(?<href>[^"']+)"?'?/mgui)
                        ?.map((s) => s.replaceAll(/'|"|href=/g, ''))
                        ?.filter((s) => !/\.(jpg|jpeg|png|gif|webp)$/.test(s)) // isn't image
                )
            )
        ]
        return {
            publicationDate: post.publicationDate ? moment(post.publicationDate).format("DD.MM.YYYY") : null,
            publicationTime: post.publicationTime ? moment(post.publicationTime * 1000).format("HH:mm") : null,
            publicationDoW: post.publicationDate ? t(`DoW.${moment(post.publicationDate).day()}`) : null,
            status: post.isDeleted ? t('ExportPostStats.publication.isDeleted') : t('ExportPostStats.publication.isActive'),
            deletionDate: post.isDeleted && post.deletedAtDate ? moment.unix(post.deletedAtDate).format("DD.MM.YYYY") : null,
            deletionTime: post.isDeleted && post.deletedAtTime ? moment.unix(post.deletedAtTime).format("HH:mm") : null,
            viewsCount: post.viewsCount,
            views12: post.views12,
            views24: post.views24,
            views48: post.views48,
            link: post.link && !post.link.startsWith('http') ? `https://${post.link}` : post.link,
            platform: post.platform,
            reactionsCount: post.reactionsCount,
            sharesCount: post.sharesCount,
            forwardsCount: post.forwardsCount,
            commentsCount: post.commentsCount,
            messageLength: removeHTMLTags(post.text).length,
            messageErid: /erid[^\w\d]{0,2}([\w\d]+)/mui.exec(post.text)?.at(1)
                || /erid[^\w\d]{0,2}([\w\d]+)/mui.exec(removeHTMLTags(post.text))?.at(1),
            messageLinks: links.filter((e) => {
                return !e.startsWith('t.me')
                    && !e.startsWith('https://t.me')
                    && !e.startsWith('http://t.me')
            }).join('; '),
            messageLinksTg: links.filter((e) => {
                return e.startsWith('t.me')
                    || e.startsWith('https://t.me')
                    || e.startsWith('http://t.me')
            })
        }
    }

    const copyToClipboard = async (e: any) => {
        e.target.setAttribute('loading', 'true')
        try {
            const data = content
                .map(getObjectForUser)
                .map((row: any) => ColumnsForBuffer.map((c: any) => c.format(row[c.key])).join("\t"))
                .join('\n')
            await navigator.clipboard.writeText(data)

            api.success({
                message: t(`ExportPostStats.copyToClipboard.successMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportPostStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const copyToClipboardForAnalytics = async () => {
        try {
            const data = content
                .map(getObjectForUser)
                .map((row: any) => ColumnsForBufferAnalytics.map((c: any) => c.format(row[c.key])).join("\t"))
                .join('\n')
            await navigator.clipboard.writeText(data)

            api.success({
                message: t(`ExportPostStats.copyToClipboard.successMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportPostStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const copyToClipboardWithHeaders = async () => {
        try {
            const data = [
                columns.map(c => c.title).join("\t"),
                ...content
                    .map(getObjectForUser)
                    .map((row: any) => ColumnsForBufferAll.map((c: any) => c.format(row[c.key])).join("\t"))
            ].join('\n')
            await navigator.clipboard.writeText(data)

            api.success({
                message: t(`ExportPostStats.copyToClipboard.successMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportPostStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const copyToClipboardForAnalytics2 = async () => {
        try {
            const data = content
                .map(getObjectForUser)
                .map((row: any) => ColumnsForBufferAnalytics2.map((c: any) => c.format(row[c.key])).join("\t"))
                .join('\n')
            await navigator.clipboard.writeText(data)

            api.success({
                message: t(`ExportPostStats.copyToClipboard.successMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportPostStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const items: MenuProps['items'] = [
        {
            label: t(`ExportPostStats.copyToClipboardForAnalytics`),
            key: 'copyToClipboardForAnalytics',
        },
        {
            label: t(`ExportPostStats.copyToClipboardForAnalytics2`),
            key: `copyToClipboardForAnalytics2`,
        },
        {
            label: t(`ExportPostStats.copyToClipboardWithHeaders`),
            key: `copyToClipboardWithHeaders`,
        }
    ]

    const handleCopyMenuClick = async (e: any) => {
        if (e.key === 'copyToClipboardForAnalytics') copyToClipboardForAnalytics()
        if (e.key === 'copyToClipboardForAnalytics2') copyToClipboardForAnalytics2()
        if (e.key === 'copyToClipboardWithHeaders') copyToClipboardWithHeaders()
    };

    return (
        <div style={{ height: '100%', position: 'relative', flexDirection: 'row', }}>
            <Space.Compact style={{ width: '100%' }}>
                <Popover
                    content={
                        <List
                            style={{ maxHeight: '50vh', paddingTop: 5, overflow: "auto" }}
                            itemLayout="horizontal"
                            loading={status === Status.isLoading}
                            dataSource={search}
                            locale={{
                                emptyText: <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t('ExportPostStats.searchArray.empty')}
                                />
                            }}
                            renderItem={(url, idx) => (
                                <List.Item key={url} style={{ height: 40, marginTop: 10, marginBottom: 0 }}>
                                    <Paragraph
                                        copyable={{
                                            icon: [
                                                <CopyOutlined style={{ color: '#9261f1' }} />,
                                                <CheckOutlined style={{ color: '#9261f1' }} />,
                                            ],
                                        }}
                                        ellipsis={{
                                            expandable: 'collapsible',
                                            symbol: (expanded) => expanded
                                                ? <UpOutlined style={{ color: '#9261f1' }} />
                                                : <DownOutlined style={{ color: '#9261f1' }} />
                                        }}
                                        style={{ left: 0, width: 450 }}
                                        editable={{
                                            icon: <EditOutlined style={{ color: '#9261f1' }} />,
                                            onChange: (value) => handleSearchChange(
                                                search.map((e, i) => i === idx ? value : e).join(' ')
                                            ),
                                        }}
                                    >
                                        {url}
                                    </Paragraph>
                                </List.Item>
                            )}
                        />
                    }
                    placement="bottomRight"
                    title={
                        <div style={{ paddingRight: 10, minWidth: 500 }}>
                            <Tooltip placement="top" title={t('ExportPostStats.searchArray.titleHelp')}>
                                <Badge count={<QuestionCircleOutlined />} style={{ color: '#9261f1', paddingLeft: 20 }}>
                                    {t('ExportPostStats.searchArray.title')}
                                </Badge>
                            </Tooltip>
                        </div>
                    }
                    trigger="click"
                    open={!!searchArrayOpened}
                    onOpenChange={handleSearchArrayOpenedChange}>
                    <Badge count={search.length} color="#9261f1" overflowCount={99} style={{ zIndex: 1 }}>
                        <Avatar
                            style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                background: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRight: 0,
                                color: '#000',
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                            shape="square"
                            size="large">
                            {!!searchArrayOpened ? (
                                <UpOutlined />
                            ) : (
                                <DownOutlined />
                            )}
                        </Avatar>
                    </Badge>
                </Popover>
                <Input placeholder={t(`ExportPostStats.search.placeholder`)}
                    size="large"
                    value={search.join(', ')}
                    onPressEnter={handleSearch}
                    onChange={e => handleSearchChange(e.target.value)}
                    onFocus={e => e.target.select()}
                    disabled={Boolean(search.length)}
                />
                <Button
                    size="large"
                    onClick={handleSearchClear}>
                    <CloseOutlined />
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSearch}
                    loading={status === Status.isLoading}>
                    {t(`ExportPostStats.search.button`)}
                </Button>
            </Space.Compact>
            <UsersManualExportButton t={t} />
            {status === Status.isEmpty && (
                <Result style={{ paddingTop: '25vh', opacity: .7 }}
                    icon={<Empty description={false} />}
                    status="warning"
                    title={t(`ExportPostStats.search.empty`)}
                />
            )}
            {status === Status.isContent && (
                <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', height: 'calc(100% - 70px)' }}>
                    <Space align="end" direction="vertical">
                        <Dropdown.Button
                            menu={{
                                items,
                                onClick: handleCopyMenuClick,
                            }}
                            icon={<DownOutlined />}
                            type="text"
                            placement="bottom"
                            onClick={copyToClipboard}
                            style={{ color: "#e7e7e7", borderTopLeftRadius: 6, borderTopRightRadius: 6, background: '#efefef', alignSelf: 'flex-end' }}
                        >
                            {t(`ExportPostStats.copyToClipboard.action`)}
                        </Dropdown.Button>
                    </Space>
                    <BaseGrid
                        columnsDesc={columns}
                        style={{ height: '100%' }}
                        data={
                            content.map((e) => ({ key: uniqid(), ...getObjectForUser(e) }))
                        }
                        onEdit={(): void => { }}
                    />
                </div>
            )}
            {status === Status.isLoading && (
                <SpinLoading progress={progress} />
            )}
            {contextHolder}
        </div>
    );
}

export default ExportPostStats;